const AGENCY_INVITATION_TOKEN_SESSION_KEY =
  "care_coordination_invitation_token";
const AGENCY_INVITATION_TOKEN_TTL_MS = 2 * 60 * 60 * 1000;

type AgencyInvitationTokenStash = {
  token: string;
  expiresAt: number;
};

export function readAgencyInvitationTokenFromHash(hash?: string): string {
  const fragment =
    hash ?? (typeof window !== "undefined" ? window.location.hash : "");
  const params = new URLSearchParams(fragment.replace(/^#/, ""));
  return (params.get("token") || "").trim();
}

export function stripAgencyInvitationTokenFromAddressBar(): void {
  if (typeof window === "undefined" || !window.location.hash) return;
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`
    );
  } catch {
    // The invitation flow can continue from in-memory token state.
  }
}

export function stashAndStripAgencyInvitationTokenFromAddressBar(): void {
  const token = readAgencyInvitationTokenFromHash();
  if (!token) return;
  stashAgencyInvitationToken(token);
  stripAgencyInvitationTokenFromAddressBar();
}

export function stashAgencyInvitationToken(token: string): void {
  const value = token.trim();
  if (!value || typeof sessionStorage === "undefined") return;
  try {
    const payload: AgencyInvitationTokenStash = {
      token: value,
      expiresAt: Date.now() + AGENCY_INVITATION_TOKEN_TTL_MS,
    };
    sessionStorage.setItem(
      AGENCY_INVITATION_TOKEN_SESSION_KEY,
      JSON.stringify(payload)
    );
  } catch {
    // sessionStorage may be unavailable.
  }
}

export function readStashedAgencyInvitationToken(): string {
  if (typeof sessionStorage === "undefined") return "";
  try {
    const raw = sessionStorage.getItem(AGENCY_INVITATION_TOKEN_SESSION_KEY);
    if (!raw) return "";
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      clearStashedAgencyInvitationToken();
      return "";
    }
    const stash = parsed as Partial<AgencyInvitationTokenStash>;
    if (
      typeof stash.token !== "string" ||
      typeof stash.expiresAt !== "number" ||
      !Number.isFinite(stash.expiresAt) ||
      stash.expiresAt <= Date.now()
    ) {
      clearStashedAgencyInvitationToken();
      return "";
    }
    return stash.token.trim();
  } catch {
    clearStashedAgencyInvitationToken();
    return "";
  }
}

export function clearStashedAgencyInvitationToken(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(AGENCY_INVITATION_TOKEN_SESSION_KEY);
  } catch {
    // sessionStorage may be unavailable.
  }
}
